import React from 'react'
import { arc, selectAll, interpolateString } from 'd3'

import Box from '@mui/material/Box'
import { darken, lighten, useTheme } from '@mui/material/styles'

const INNER_RADIUS = 100
const OUT_RADIUS = 140
const SELECTION_RADIUS = 10

export interface DonutData {
  id: string
  label?: string
  color: string
  value: number
}

export interface DonutProps {
  data: DonutData[]
  hoveredId?: string
  onClick?: (id: string) => void
  onMouseEnter?: (id: string) => void
  onMouseLeave?: (id: string) => void
  selectedId?: string
  size?: number
}

const Donut: React.FC<React.PropsWithChildren<DonutProps>> = ({
  data,
  hoveredId,
  onClick,
  onMouseEnter,
  onMouseLeave,
  selectedId,
  size = 300,
  children,
}) => {
  const theme = useTheme()
  let offset = 0

  const handleKeyDown = (event: React.KeyboardEvent, id: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick?.(id)
    }
  }

  const handleFocus = (id: string) => {
    onMouseEnter?.(id)
  }

  const handleBlur = (id: string) => {
    onMouseLeave?.(id)
  }

  React.useEffect(() => {
    selectAll('.arc-outer')
      .transition('roll')
      .duration(500)
      .attrTween('transform', () => interpolateString('rotate(0)', 'rotate(360)'))
  }, [])

  return (
    <Box height={size} position="relative" width={size}>
      <Box height="100%" width="100%">
        {children}
      </Box>
      <svg
        height={size}
        style={{ position: 'absolute', top: 0, left: 0, touchAction: 'manipulation' }}
        viewBox={`-150 -150 300 300`}
        width={size}
      >
        <style>
          {`
            .arc-outer:focus,
            .arc-outer:focus-visible {
              stroke: ${theme.palette.primary.main};
              stroke-width: 3;
              stroke-opacity: 0.8;
            }
          `}
        </style>
        <g>
          {data.map((item) => {
            const startAngle = offset
            offset = offset + 2 * Math.PI * (item.value / 100)
            const endAngle = offset

            const segment = arc().cornerRadius(4)({
              innerRadius: INNER_RADIUS,
              outerRadius:
                OUT_RADIUS +
                (item.id === selectedId || item.id === hoveredId ? SELECTION_RADIUS : 0),
              startAngle,
              endAngle,
              padAngle: 0.025,
            }) as string

            return (
              <path
                aria-label={item.label || `Segment ${item.id}`}
                aria-pressed={item.id === selectedId}
                className="arc-outer"
                d={segment}
                fill={item.color}
                key={item.id}
                onBlur={() => handleBlur(item.id)}
                onClick={() => onClick?.(item.id)}
                onFocus={() => handleFocus(item.id)}
                onKeyDown={(e) => handleKeyDown(e, item.id)}
                onMouseEnter={() => onMouseEnter?.(item.id)}
                onMouseLeave={() => onMouseLeave?.(item.id)}
                role="button"
                stroke={
                  theme.palette.mode === 'light'
                    ? darken(item.color, 0.3)
                    : lighten(item.color, 0.3)
                }
                strokeWidth={2}
                style={{
                  cursor: 'pointer',
                  outline: 'none',
                }}
                tabIndex={0}
              />
            )
          })}
        </g>
      </svg>
    </Box>
  )
}

export default Donut
